import { useId, useState, type KeyboardEvent, type ClipboardEvent } from "react";

type PennTagInputProps = {
  /** Current tags. Controlled — pair with `onChange`. */
  value: string[];
  onChange: (tags: string[]) => void;
  id?: string;
  disabled?: boolean;
  /** Field-level error styling (e.g. from a zod resolver). */
  error?: boolean;
  placeholder?: string;
  /**
   * Characters that commit the current draft into a tag (in addition to Enter).
   * Pasted text is also split on these plus newlines. Defaults to comma.
   */
  separators?: string[];
  /** Reject duplicate tags case-insensitively. Default true. */
  dedupe?: boolean;
  /**
   * Optional per-tag validator. A token that fails is not added; it stays in the
   * draft input with an error ring so the user can correct it (no silent drop).
   */
  validate?: (tag: string) => boolean;
  className?: string;
};

/**
 * Reusable chip/tag input for multi-value fields (e.g. machine codes, email lists).
 * Replaces ad-hoc "comma-separated" text inputs across web-admin.
 *
 * Commits a tag on Enter or any configured separator, splits pasted lists, and
 * removes the last tag on Backspace when the draft is empty.
 */
export const PennTagInput = ({
  value,
  onChange,
  id,
  disabled = false,
  error = false,
  placeholder,
  separators = [","],
  dedupe = true,
  validate,
  className = "",
}: PennTagInputProps) => {
  const [draft, setDraft] = useState("");
  const [draftInvalid, setDraftInvalid] = useState(false);
  // Non-semantic field name so the browser can't match this to a saved
  // email/contact profile and offer autofill suggestions.
  const autofillBlockName = `pti-${useId()}`;

  const splitRegex = new RegExp(
    `[${separators.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("")}\\n\\r]+`
  );

  const isDuplicate = (tag: string) =>
    dedupe && value.some((t) => t.toLowerCase() === tag.toLowerCase());

  /** Commit a single token; returns true if it was added. */
  const commit = (raw: string): boolean => {
    const tag = raw.trim();
    if (!tag) return false;
    if (validate && !validate(tag)) {
      setDraftInvalid(true);
      return false;
    }
    if (isDuplicate(tag)) return false;
    onChange([...value, tag]);
    return true;
  };

  const commitDraft = () => {
    if (commit(draft)) {
      setDraft("");
      setDraftInvalid(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || separators.includes(e.key)) {
      e.preventDefault();
      commitDraft();
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!splitRegex.test(text)) return;
    e.preventDefault();
    const tokens = text.split(splitRegex).map((t) => t.trim()).filter(Boolean);
    const added: string[] = [];
    const rejected: string[] = [];
    for (const token of tokens) {
      if (validate && !validate(token)) rejected.push(token);
      else if (!isDuplicate(token) && !added.includes(token)) added.push(token);
    }
    if (added.length) onChange([...value, ...added]);
    setDraft(rejected.join(", "));
    setDraftInvalid(rejected.length > 0);
  };

  const removeAt = (index: number) =>
    onChange(value.filter((_, i) => i !== index));

  const showError = error || draftInvalid;

  return (
    <div
      className={[
        "flex w-full flex-wrap items-center gap-2 rounded-xl border px-2 py-2 text-sm transition",
        "focus-within:ring-2 focus-within:ring-penn-500 focus-within:border-penn-500",
        showError ? "border-error bg-error/5" : "border-gray-200 bg-gray-50 hover:border-penn-500",
        disabled ? "opacity-50 cursor-not-allowed" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {value.map((tag, i) => (
        <span
          key={`${tag}-${i}`}
          className="inline-flex items-center gap-1 rounded-lg bg-penn-100 px-2 py-1 text-xs font-medium text-penn-700"
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label={`Remove ${tag}`}
              className="leading-none text-penn-700/70 transition hover:text-penn-900"
            >
              &times;
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          id={id}
          name={autofillBlockName}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (draftInvalid) setDraftInvalid(false);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={commitDraft}
          aria-invalid={showError}
          placeholder={value.length === 0 ? placeholder : ""}
          // Suppress the browser's saved-email autofill dropdown and
          // password-manager overlays on this free-text token input.
          // "new-password" is the only value Chrome reliably honours here;
          // "off" is ignored on fields it heuristically reads as email.
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          className="min-w-[8ch] flex-1 bg-transparent px-1 py-1 text-penn-text placeholder-gray-400 outline-none"
        />
      )}
    </div>
  );
};

PennTagInput.displayName = "PennTagInput";
